import React, { useState } from "react";
import { Switch, Tooltip, message } from "antd";
import { useAppDispatch } from "app/hooks";
import { updateUpcomingEventList } from "models/Slices/EventManagementSlice";
import { EventService } from "services/SkyboxServices";
import { LoggerService } from "sharedServices";
import { hasValidSeatMapUrl } from "utils/commonFunction";

const SEAT_MAP_REQUIRED_MESSAGE =
  "A seat map URL is required before Auto Listing can be enabled";

const ToggleAutoListing: React.FC<{
  eventId: number;
  isAutoListingEnabled: boolean;
  seatMapUrl: string | null;
}> = ({ eventId, isAutoListingEnabled, seatMapUrl }) => {
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);

  const isSeatMapAvailable = hasValidSeatMapUrl(seatMapUrl);

  // Display-only suppression. The stored flag defaults to TRUE, so an event that
  // has never had a seat map URL would otherwise read ON while being unusable.
  // Rendering never writes: no request is issued here on mount.
  //
  // Boolean(...) is required, not cosmetic: if isAutoListingEnabled ever arrives
  // as undefined/null (e.g. the field is momentarily absent from the API row),
  // `isSeatMapAvailable && isAutoListingEnabled` evaluates to that same
  // undefined/null rather than false. Passing a non-boolean `checked` to antd's
  // Switch makes React treat it as an uncontrolled component, so its visible
  // state stops following this prop entirely — same fix as EventHeader.tsx's
  // Auto Listing checkbox.
  const checked = isSeatMapAvailable && Boolean(isAutoListingEnabled);

  const handleChange = async (next: boolean) => {
    if (isSaving || !isSeatMapAvailable) return; // AC10 in-flight guard
    setIsSaving(true);

    try {
      const response = await new EventService().saveAutoListingStatus(
        eventId,
        next
      );

      // Commit the PERSISTED value, never `next` — this is what makes the
      // switch pessimistic and removes any need for revert logic.
      dispatch(
        updateUpcomingEventList({
          event_id: eventId,
          is_auto_listing_enabled:
            response.data.data.is_auto_listing_enabled,
        })
      );

      message.success({
        content: `Auto Listing turned ${next ? "on" : "off"}.`,
        className: "toastMsg savedSuccess",
      });
    } catch (error: any) {
      // Dispatch nothing: the switch never moved, so it stays where it was.
      message.error({
        content:
          error?.code === 422
            ? SEAT_MAP_REQUIRED_MESSAGE
            : "Something went wrong",
        className: "toastMsg savedFailed",
      });
      await new LoggerService().log({
        payload: error,
        function_name: "toggleAutoListing",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Tooltip
      title={
        isSeatMapAvailable ? "Auto Listing" : SEAT_MAP_REQUIRED_MESSAGE
      }
      placement="top"
    >
      {/* span wrapper: antd Tooltip needs a non-disabled child to bind to */}
      <span>
        <Switch
          size="small"
          className="yesNo autoListingToggle"
          checked={checked}
          disabled={!isSeatMapAvailable}
          loading={isSaving}
          onChange={handleChange}
        />
      </span>
    </Tooltip>
  );
};

export default ToggleAutoListing;
